import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, Upload, Loader2, Send, RotateCcw, Play, Pause, Check,
  Trash2, X, Sparkles, Heart,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatSec(s) {
  s = Math.max(0, Math.floor(s));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

function fileToBase64(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

function CountdownTimer({ seconds }) {
  const [remaining, setRemaining] = useState(seconds);
  const [running, setRunning] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    setRemaining(seconds);
    setRunning(false);
  }, [seconds]);

  useEffect(() => {
    if (!running) return;
    intervalRef.current = setInterval(() => {
      setRemaining(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current);
          setRunning(false);
          toast.success('Tiden är ute — och du KLARADE det. Kram 💗', { duration: 4000 });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [running]);

  const pct = seconds ? ((seconds - remaining) / seconds) * 100 : 0;

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={() => setRunning(r => !r)}
        className={`neo-btn px-4 py-2 font-bold flex items-center gap-2 ${
          running ? 'bg-[#FFC8DD]' : 'bg-[#B9FBC0]'
        }`}
        data-testid="kickstart-timer-btn"
      >
        {running ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
        {running ? 'Pausa' : `Kör ${formatSec(seconds)}`}
      </button>
      <div className="flex-1">
        <div className="text-xs font-bold text-gray-700 mb-1">
          {running ? `Kvar: ${formatSec(remaining)}` : `Räcker med ${formatSec(seconds)}`}
        </div>
        <div className="w-full h-2 rounded-full bg-white border border-black overflow-hidden">
          <div
            className="h-full bg-black transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function StepCard({ step, big = false, idx }) {
  return (
    <div
      className={`neo-card ${big ? 'p-6 bg-white' : 'p-4 bg-white/70'}`}
      data-testid={big ? 'first-step-card' : `next-step-${idx}`}
    >
      <div className="flex items-start gap-3">
        <div className={`${big ? 'w-14 h-14 text-4xl' : 'w-10 h-10 text-2xl'} rounded-2xl border-2 border-black flex items-center justify-center bg-[#FDF0D5] flex-shrink-0`}>
          {step.emoji || '✨'}
        </div>
        <div className="flex-1 min-w-0">
          {big ? (
            <>
              <div className="text-xs font-bold uppercase tracking-widest text-gray-600 mb-1">
                Börja här
              </div>
              <div className="text-2xl md:text-3xl font-['Fredoka'] font-bold leading-tight">
                {step.action}
              </div>
              {step.why && (
                <div className="text-sm text-gray-700 mt-2">{step.why}</div>
              )}
            </>
          ) : (
            <>
              <div className="text-sm font-bold text-gray-500 mb-0.5">
                Sen om du orkar mer
              </div>
              <div className="font-['Fredoka'] font-bold leading-snug">
                {step.action}
              </div>
              {step.why && (
                <div className="text-xs text-gray-600 mt-1">{step.why}</div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function UploadPanel({ onAnalyzed }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const cameraRef = useRef(null);
  const uploadRef = useRef(null);

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    const b64 = await fileToBase64(f);
    setPreview(b64);
  };

  const clear = () => {
    setFile(null);
    setPreview('');
    setNote('');
  };

  const analyze = async () => {
    if (!preview) {
      toast.error('Välj eller ta ett kort först 💗');
      return;
    }
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/kickstart/analyze`,
        { image_base64: preview, note },
        { withCredentials: true, timeout: 60000 }
      );
      toast.success('Klart! Här är din startpunkt ✨');
      onAnalyzed(data);
      clear();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte analysera bilden');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="neo-card p-5 bg-white space-y-4">
      <div>
        <h2 className="text-2xl md:text-3xl font-['Fredoka'] font-bold leading-tight">
          Ta ett kort på röran 📸
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Du behöver inte städa allt. Jag hittar EN pytteliten startpunkt så du bara behöver börja. 💗
        </p>
      </div>

      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="preview"
            className="w-full max-h-72 object-cover rounded-2xl border-2 border-black"
          />
          <button
            onClick={clear}
            className="absolute top-2 right-2 w-8 h-8 bg-white rounded-full border-2 border-black flex items-center justify-center"
            data-testid="clear-preview-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <button
            onClick={() => cameraRef.current?.click()}
            className="neo-btn py-5 bg-[#FFC8DD] flex flex-col items-center gap-2"
            data-testid="camera-btn"
          >
            <Camera className="w-6 h-6" />
            <span className="font-bold text-sm">Ta kort</span>
          </button>
          <button
            onClick={() => uploadRef.current?.click()}
            className="neo-btn py-5 bg-[#A2D2FF] flex flex-col items-center gap-2"
            data-testid="upload-btn"
          >
            <Upload className="w-6 h-6" />
            <span className="font-bold text-sm">Ladda upp</span>
          </button>
          <input
            ref={cameraRef}
            type="file"
            accept="image/*"
            capture="environment"
            hidden
            onChange={pick}
            data-testid="camera-input"
          />
          <input
            ref={uploadRef}
            type="file"
            accept="image/*"
            hidden
            onChange={pick}
            data-testid="upload-input"
          />
        </div>
      )}

      <div>
        <label className="text-sm font-bold mb-1 block">
          Vad känns värst just nu? (valfritt)
        </label>
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="neo-input w-full"
          placeholder="t.ex. Överväldigad av köket / disken hopar sig"
          maxLength={300}
          data-testid="kickstart-note-input"
        />
      </div>

      <button
        onClick={analyze}
        disabled={busy || !preview}
        className="neo-btn w-full py-3 bg-[#B9FBC0] disabled:opacity-40 flex items-center justify-center gap-2 font-bold"
        data-testid="analyze-btn"
      >
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Sparkles className="w-5 h-5" />}
        {busy ? 'Tittar på bilden...' : 'Hjälp mig börja ✨'}
      </button>
    </div>
  );
}

function ResultPanel({ snap, onReset, onPushed, onDelete }) {
  const a = snap?.analysis || {};
  const first = a.first_step;
  const nexts = a.next_steps || [];

  const push = async () => {
    try {
      const { data } = await axios.post(
        `${API}/kickstart/${snap.id}/push-to-flow`,
        {},
        { withCredentials: true }
      );
      toast.success(`${data.created} steg skickade till Idag-listan ✨`);
      onPushed?.();
    } catch (e) {
      toast.error('Kunde inte skicka');
    }
  };

  return (
    <div className="space-y-4" data-testid="kickstart-result">
      <div className="neo-card p-5 bg-gradient-to-br from-[#FDF0D5] via-[#FFE5B4] to-[#FFC8DD]">
        <div className="flex items-start gap-3">
          <Heart className="w-5 h-5 flex-shrink-0 mt-1" />
          <div>
            <div className="text-lg font-['Fredoka'] font-bold leading-snug">
              {a.encouragement}
            </div>
            {a.clutter_read && (
              <div className="text-sm text-gray-700 mt-1">
                Vad jag ser: {a.clutter_read}
              </div>
            )}
          </div>
        </div>
      </div>

      {first && <StepCard step={first} big />}

      {first?.time_est_sec ? (
        <div className="neo-card p-4 bg-white">
          <CountdownTimer seconds={first.time_est_sec} />
        </div>
      ) : null}

      {nexts.length > 0 && (
        <div className="space-y-2">
          {nexts.map((s, i) => (
            <StepCard key={i} step={s} idx={i} />
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={push}
          className="neo-btn py-3 bg-[#B9FBC0] flex items-center justify-center gap-2"
          data-testid="push-to-flow-btn"
        >
          <Send className="w-4 h-4" />
          <span className="font-bold text-sm">Skicka till Idag-lista</span>
        </button>
        <button
          onClick={onReset}
          className="neo-btn py-3 bg-white flex items-center justify-center gap-2"
          data-testid="reset-btn"
        >
          <RotateCcw className="w-4 h-4" />
          <span className="font-bold text-sm">Ta nytt kort</span>
        </button>
      </div>

      {snap.image_base64 && (
        <details className="neo-card p-3 bg-white/70">
          <summary className="text-xs font-bold text-gray-600 cursor-pointer">
            Visa bilden jag tittade på
          </summary>
          <img
            src={`data:image/jpeg;base64,${snap.image_base64}`}
            alt="snap"
            className="mt-2 w-full max-h-72 object-cover rounded-xl border border-black/20"
          />
        </details>
      )}

      <div className="text-center">
        <button
          onClick={() => onDelete?.(snap.id)}
          className="text-xs text-gray-500 hover:text-red-500 inline-flex items-center gap-1"
        >
          <Trash2 className="w-3 h-3" /> Ta bort den här analysen
        </button>
      </div>
    </div>
  );
}

function HistoryStrip({ history, onOpen }) {
  if (!history || history.length === 0) return null;
  return (
    <div className="neo-card p-4 bg-white">
      <div className="text-sm font-bold mb-2">Tidigare startpunkter 🌸</div>
      <div className="space-y-2">
        {history.slice(0, 5).map(item => (
          <button
            key={item.id}
            onClick={() => onOpen(item.id)}
            className="w-full text-left p-2 rounded-lg border border-black/20 hover:bg-gray-50 flex items-center gap-2"
            data-testid={`history-item-${item.id}`}
          >
            <span className="text-xl">{item.analysis?.first_step?.emoji || '✨'}</span>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-bold truncate">
                {item.analysis?.first_step?.action || '—'}
              </div>
              <div className="text-xs text-gray-500">
                {new Date(item.created_at).toLocaleString('sv-SE', { dateStyle: 'short', timeStyle: 'short' })}
              </div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MessKickstart() {
  const [snap, setSnap] = useState(null);
  const [history, setHistory] = useState([]);
  const [loadingHist, setLoadingHist] = useState(true);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/kickstart/history?limit=10`, { withCredentials: true });
      setHistory(data.items || []);
    } catch (e) {
      // silent
    } finally {
      setLoadingHist(false);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  const openHistory = async (id) => {
    try {
      const { data } = await axios.get(`${API}/kickstart/${id}`, { withCredentials: true });
      setSnap(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Kunde inte öppna');
    }
  };

  const deleteSnap = async (id) => {
    if (!window.confirm('Ta bort denna analys?')) return;
    try {
      await axios.delete(`${API}/kickstart/${id}`, { withCredentials: true });
      toast.success('Borttagen');
      if (snap?.id === id) setSnap(null);
      loadHistory();
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  return (
    <div className="space-y-4 max-w-2xl mx-auto" data-testid="mess-kickstart">
      {snap ? (
        <ResultPanel
          snap={snap}
          onReset={() => setSnap(null)}
          onPushed={loadHistory}
          onDelete={deleteSnap}
        />
      ) : (
        <>
          <UploadPanel onAnalyzed={(data) => { setSnap(data); loadHistory(); }} />
          {!loadingHist && <HistoryStrip history={history} onOpen={openHistory} />}
        </>
      )}
    </div>
  );
}
